import React, { useState, useEffect } from "react";
import {
    Button, Card, Form, Input, Alert, Checkbox, Row, Col,
    Typography, Flex, Steps,
} from "antd";
import {
    UserOutlined, FolderOutlined, FileTextOutlined, CheckCircleOutlined,
    MailOutlined, PhoneOutlined, IdcardOutlined, CarOutlined,
    ArrowLeftOutlined, ArrowRightOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";
import Footer               from "./snipets/Footer";
import { User }             from "../models/User";
import { AuthenticationService } from "../services/AuthenticationService";
import { Bank, BankAccount }     from "../models/Bank";

import standard_bank from "../assets/standard_bank-logo_brandlogos.net_aqqyw.png";
import capitec       from "../assets/960px-Capitec_Bank_logo.svg.png";
import fnb           from "../assets/First_National_Bank_Logo.png";
import absa          from "../assets/ABSA_Logo.svg";
import africanBank   from "../assets/African_Bank_logo.svg";
import nedbank       from "../assets/Nedbank_logo.svg";
import tyme          from "../assets/TymeBank_Logo.jpg";

const { Title, Text, Link } = Typography;

const CAPITEC_BLUE = "#2F70EF";

const BANKS = [
    { key: "capitec", dbId: 1, name: "Capitec",       value: "capitec", recommended: true,  logo: capitec       },
    { key: "fnb",     dbId: 2, name: "FNB",           value: "fnb",     recommended: false, logo: fnb           },
    { key: "absa",    dbId: 3, name: "ABSA",          value: "absa",    recommended: false, logo: absa          },
    { key: "std",     dbId: 4, name: "Standard Bank", value: "std",     recommended: false, logo: standard_bank },
    { key: "ned",     dbId: 5, name: "Nedbank",       value: "ned",     recommended: false, logo: nedbank       },
    { key: "tyme",    dbId: 6, name: "TymeBank",      value: "tyme",    recommended: false, logo: tyme          },
    { key: "african", dbId: 7, name: "African Bank",  value: "african", recommended: false, logo: africanBank   },
];

interface PersonalInfo {
    firstName: string; lastName: string; email: string;
    phone: string; idNumber: string; vehicleRegistrationNumber: string;
}
interface Documents  { driversLicense: File | null; vehicleRegDisk: File | null; bankStatement: File | null; }
interface Banking    { bankName: string; }

// ── Upload Box ────────────────────────────────────────────────────────────────

const UploadBox: React.FC<{ label: string; file: File | null; onSelect: (f: File) => void }> = ({ label, file, onSelect }) => {
    const ref = React.useRef<HTMLInputElement>(null);
    return (
        <div
            onClick={() => ref.current?.click()}
            style={{
                border: `2px dashed ${file ? "#00AF66" : "#D0D0D0"}`,
                borderRadius: 14, padding: "32px 20px", textAlign: "center",
                cursor: "pointer", background: file ? "#E6F7EF" : "#FAFAFA",
            }}
        >
            <input ref={ref} type="file" accept="image/*,.pdf" style={{ display: "none" }}
                   onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])} />
            <CheckCircleOutlined style={{ fontSize: 32, color: file ? "#00AF66" : "#BDBDBD", display: "block", marginBottom: 12 }} />
            <Text strong style={{ color: file ? "#00AF66" : "#1A1A2E", display: "block", marginBottom: 4 }}>{label}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{file ? file.name : "Click to upload — JPG, PNG or PDF"}</Text>
        </div>
    );
};

// ── Bank Card ─────────────────────────────────────────────────────────────────

const BankCard: React.FC<{ bank: typeof BANKS[0]; selected: boolean; onSelect: () => void }> = ({ bank, selected, onSelect }) => (
    <div
        onClick={onSelect}
        style={{
            border: `2px solid ${selected ? "#00AF66" : bank.recommended ? CAPITEC_BLUE : "#E0E0E0"}`,
            borderRadius: 12, padding: "12px 8px", textAlign: "center",
            cursor: "pointer", background: selected ? "#E6F7EF" : "white",
            position: "relative",
        }}
    >
        {bank.recommended && (
            <div style={{
                position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                background: CAPITEC_BLUE, color: "white", fontSize: 9, fontWeight: 900,
                padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
            }}>
                ⚡ FASTEST PAYOUT
            </div>
        )}
        <div style={{ height: 36, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <img src={bank.logo} alt={bank.name}
                 style={{ maxHeight: 32, maxWidth: "90%", objectFit: "contain" }}
                 onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
        </div>
        <Text style={{ fontSize: 11, fontWeight: selected ? 800 : 600, color: selected ? "#00AF66" : "#555" }}>
            {bank.name}
        </Text>
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

// Deliberate breathing room below the fixed header — not a guess, added on top
// of the nav's real height so the two stay in sync if the header ever resizes.
const CONTENT_TOP_GAP = 32;

const DriverRegisterPage: React.FC = () => {
    const navigate = useNavigate();

    const [step,          setStep]          = useState(0);
    const [error,         setError]         = useState("");
    const [loading,       setLoading]       = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const [personal, setPersonal] = useState<PersonalInfo>({
        firstName: "", lastName: "", email: "", phone: "", idNumber: "", vehicleRegistrationNumber: "",
    });
    const [docs,    setDocs]    = useState<Documents>({ driversLicense: null, vehicleRegDisk: null, bankStatement: null });
    const [banking, setBanking] = useState<Banking>({ bankName: "" });

    // Prefill from the logged-in user's own profile, matching the mobile app.
    // This is the intended shape of the flow: driver registration only adds
    // driver-specific info to an *existing* account (see backend, which 404s
    // if no user exists with the submitted email) rather than creating a new
    // one — prefilling avoids someone typing a different email than the one
    // they're logged in with and hitting that 404 at the very last step.
    useEffect(() => {
        AuthenticationService.getUserProfile().then((user: User) => {
            setPersonal((prev) => ({
                ...prev,
                firstName: user.name,
                lastName:  user.surname,
                email:     user.email,
                phone:     user.phone,
            }));
        }).catch(() => {
            // Not logged in / profile fetch failed — leave fields blank,
            // submission will surface the "please log in / register first" error.
        });
    }, []);

    const setP = (field: keyof PersonalInfo) =>
        (e: any) => setPersonal((prev) => ({ ...prev, [field]: e.target?.value ?? e }));

    const validateStep0 = () => {
        const { firstName, lastName, email, phone, idNumber, vehicleRegistrationNumber } = personal;
        if (!firstName || !lastName || !email || !phone || !idNumber || !vehicleRegistrationNumber) {
            setError("Please fill in all required fields."); return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) { setError("Please enter a valid email address."); return false; }
        setError(""); return true;
    };

    const validateStep1 = () => {
        if (!docs.driversLicense) { setError("Please upload your driver's license.");            return false; }
        if (!docs.vehicleRegDisk) { setError("Please upload your vehicle registration disk.");   return false; }
        if (!banking.bankName)    { setError("Please select your bank.");                         return false; }
        setError(""); return true;
    };

    const validateStep2 = () => {
        if (!acceptedTerms) { setError("Please accept the terms and conditions to continue."); return false; }
        setError(""); return true;
    };

    const handleNext = () => {
        if (step === 0 && validateStep0()) setStep(1);
        if (step === 1 && validateStep1()) setStep(2);
    };

    const handleSubmit = async () => {
        if (!validateStep2()) return;
        setLoading(true);
        setError("");
        try {
            const licenseUpload = await AuthenticationService.uploadFile(docs.driversLicense!);
            const regDiskUpload = await AuthenticationService.uploadFile(docs.vehicleRegDisk!);
            const bankStmtUpload = docs.bankStatement ? await AuthenticationService.uploadFile(docs.bankStatement) : null;

            const user                       = new User();
            user.name                        = personal.firstName;
            user.surname                     = personal.lastName;
            user.email                       = personal.email;
            user.phone                       = personal.phone;
            user.id_number                   = personal.idNumber;
            user.vehicle_registration_number = personal.vehicleRegistrationNumber;
            user.drivers_license             = licenseUpload.url;
            // Confirmed against the mobile app: id_document is the vehicle
            // registration disk upload, not an ID/passport photo.
            user.id_document                 = regDiskUpload.url;
            user.bank_statement              = bankStmtUpload?.url ?? "";

            const bank        = new Bank();
            bank.id           = BANKS.find(b => b.value === banking.bankName)?.dbId ?? 0;
            const account     = new BankAccount();
            account.bank      = bank;

            await AuthenticationService.registerDriver(user);
            setStep(3);
        } catch (err: any) {
            const s = err?.response?.status || err?.status;
            if (s === 401)      setError("Not authorized. Please log in again.");
            else if (s === 400) setError("Invalid data. Please check your inputs.");
            else if (s === 404) setError("No account found with this email. Please create a regular account first, then apply to drive.");
            else if (s === 409) setError("An account with this email already exists.");
            else if (s >= 500)  setError("Server error. Please try again later.");
            else                setError("Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <TopNavigation />

            <Flex
                align="flex-start"
                justify="center"
                style={{
                    minHeight: "100dvh",
                    background: "#f5f5f0",
                    padding: `${HEADER_HEIGHT + CONTENT_TOP_GAP}px clamp(16px, 4vw, 24px) 40px`,
                }}
            >
                <div style={{ width: "100%", maxWidth: 680 }}>

                    {step < 3 && (
                        <>
                            <Title level={3} style={{ margin: "0 0 4px" }}>Driver Registration</Title>
                            <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                                Join our delivery team in three simple steps
                            </Text>
                            <Steps
                                current={step}
                                style={{ marginBottom: 32 }}
                                items={[
                                    { title: "Personal Info", icon: <UserOutlined />      },
                                    { title: "Documents",     icon: <FolderOutlined />    },
                                    { title: "Terms",         icon: <FileTextOutlined />  },
                                ]}
                            />
                        </>
                    )}

                    <Card>
                        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}

                        {/* ── Step 0: Personal Info ── */}
                        {step === 0 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 24 }}>
                                    <UserOutlined /> Personal Information
                                </Title>
                                <Form layout="vertical">
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="First Name *">
                                                <Input size="large" placeholder="First name" value={personal.firstName} onChange={setP("firstName")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Last Name *">
                                                <Input size="large" placeholder="Last name" value={personal.lastName} onChange={setP("lastName")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Email Address *">
                                                <Input size="large" prefix={<MailOutlined />} placeholder="email@example.com" type="email" value={personal.email} onChange={setP("email")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Phone Number *">
                                                <Input size="large" prefix={<PhoneOutlined />} placeholder="0XX XXX XXXX" type="tel" value={personal.phone} onChange={setP("phone")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="ID / Passport Number *">
                                                <Input size="large" prefix={<IdcardOutlined />} placeholder="ID or passport number" value={personal.idNumber} onChange={setP("idNumber")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Vehicle Registration Number *">
                                                <Input size="large" prefix={<CarOutlined />} placeholder="e.g. CA 123-456" value={personal.vehicleRegistrationNumber} onChange={setP("vehicleRegistrationNumber")} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>
                                <Button type="primary" size="large" block onClick={handleNext} icon={<ArrowRightOutlined />} iconPosition="end">
                                    Next — Documents &amp; Banking
                                </Button>
                            </>
                        )}

                        {/* ── Step 1: Documents & Banking ── */}
                        {step === 1 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 20 }}>
                                    <FolderOutlined /> Document Uploads
                                </Title>
                                <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                                    <Col xs={24} sm={8}>
                                        <UploadBox label="Driver's License" file={docs.driversLicense}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, driversLicense: f }))} />
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <UploadBox label="Vehicle Registration Disk" file={docs.vehicleRegDisk}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, vehicleRegDisk: f }))} />
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <UploadBox label="Bank Statement (optional)" file={docs.bankStatement}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, bankStatement: f }))} />
                                    </Col>
                                </Row>

                                <Title level={5} type="success" style={{ marginBottom: 12 }}>Banking Details</Title>

                                <Alert
                                    type="info"
                                    style={{ marginBottom: 20, borderLeft: `4px solid ${CAPITEC_BLUE}`, background: "#EEF4FE" }}
                                    message={<Text strong style={{ color: CAPITEC_BLUE }}>Faster Payouts with Capitec</Text>}
                                    description="We highly recommend using a Capitec Bank account — payouts are processed significantly faster compared to other banks."
                                    icon={<img src={capitec} alt="Capitec" style={{ width: 32, objectFit: "contain" }} />}
                                    showIcon
                                />

                                <Text strong style={{ display: "block", marginBottom: 12 }}>Select Your Bank *</Text>
                                <Row gutter={[12, 12]} style={{ marginBottom: 32 }}>
                                    {BANKS.map((bank) => (
                                        <Col key={bank.key} xs={12} sm={6}>
                                            <BankCard
                                                bank={bank}
                                                selected={banking.bankName === bank.value}
                                                onSelect={() => setBanking({ bankName: bank.value })}
                                            />
                                        </Col>
                                    ))}
                                </Row>

                                <Row gutter={12}>
                                    <Col xs={24} sm={8}>
                                        <Button size="large" block icon={<ArrowLeftOutlined />} onClick={() => { setError(""); setStep(0); }}>
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Button type="primary" size="large" block onClick={handleNext} icon={<ArrowRightOutlined />} iconPosition="end">
                                            Next — Terms &amp; Conditions
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* ── Step 2: Terms ── */}
                        {step === 2 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 20 }}>
                                    <FileTextOutlined /> Terms &amp; Conditions
                                </Title>

                                <div style={{
                                    background: "#f5f5f0", borderRadius: 10, border: "1px solid #E0E0E0",
                                    padding: "16px 20px", marginBottom: 24,
                                    maxHeight: 360, overflowY: "auto",
                                    fontSize: 13, color: "#555", lineHeight: 1.8,
                                }}>
                                    <p><strong>1. Independent Contractor</strong><br />You are registered as a self-employed independent contractor and not an employee. You are responsible for your own taxes, insurance, and compliance with applicable laws.</p>
                                    <p><strong>2. Delivery Standards</strong><br />You agree to deliver orders promptly, professionally, and in accordance with our delivery guidelines. Orders must be handled with care and delivered to the correct address.</p>
                                    <p><strong>3. Earnings &amp; Payouts</strong><br />Earnings are calculated per completed delivery. Payouts are processed weekly to your registered bank account. We reserve the right to withhold payment for fraudulent or incomplete deliveries.</p>
                                    <p><strong>4. Code of Conduct</strong><br />You agree to treat customers, restaurant partners, and support staff with respect at all times. Misconduct may result in suspension or permanent removal from the platform.</p>
                                    <p><strong>5. Vehicle &amp; Equipment</strong><br />You are responsible for maintaining your own vehicle or delivery equipment in a roadworthy and safe condition. We are not liable for any vehicle-related costs or incidents.</p>
                                    <p><strong>6. Data &amp; Privacy</strong><br />Your personal information is collected and processed in accordance with our Privacy Policy and applicable South African data protection laws (POPIA).</p>
                                    <p><strong>7. Termination</strong><br />Either party may terminate this agreement at any time. We reserve the right to deactivate your account for violation of these terms without prior notice.</p>
                                </div>

                                <Checkbox checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} style={{ marginBottom: 24 }}>
                                    <Text strong>I have read and agree to the Driver Terms &amp; Conditions</Text>
                                </Checkbox>

                                <Row gutter={12}>
                                    <Col xs={24} sm={8}>
                                        <Button size="large" block icon={<ArrowLeftOutlined />} onClick={() => { setError(""); setStep(1); }}>
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Button type="primary" size="large" block loading={loading} disabled={loading || !acceptedTerms} onClick={handleSubmit}>
                                            <CheckCircleOutlined /> Submit Application
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* ── Step 3: Success ── */}
                        {step === 3 && (
                            <Flex vertical align="center" gap="large" style={{ padding: "32px 0", textAlign: "center" }}>
                                <div style={{
                                    width: 96, height: 96, borderRadius: "50%",
                                    background: "#E6F7EF", border: "3px solid #00AF66",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <CheckCircleOutlined style={{ fontSize: 48, color: "#00AF66" }} />
                                </div>

                                <div>
                                    <Title level={3} style={{ margin: "0 0 4px" }}>Application Submitted!</Title>
                                    <Title level={5} type="success" style={{ margin: 0 }}>Welcome to the team 🎉</Title>
                                </div>

                                <Card style={{ textAlign: "left", width: "100%" }}>
                                    {[
                                        { icon: <ClockCircleOutlined />, title: "Review in progress", desc: <>Our team will review your application within <Text strong>5 working days</Text>.</> },
                                        { icon: <MailOutlined />,        title: "Check your email",   desc: "We'll send you a confirmation email once your account has been approved." },
                                        { icon: <PhoneOutlined />,       title: "Need help?",         desc: "Contact our driver support team if you have any questions about your application." },
                                    ].map((item, i) => (
                                        <Flex key={i} gap={12} align="flex-start" style={{ marginBottom: i < 2 ? 16 : 0 }}>
                                            <span style={{ color: "#00AF66", fontSize: 20, marginTop: 2 }}>{item.icon}</span>
                                            <div>
                                                <Text strong style={{ display: "block" }}>{item.title}</Text>
                                                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>{item.desc}</Text>
                                            </div>
                                        </Flex>
                                    ))}
                                </Card>

                                <Button type="primary" size="large" block onClick={() => navigate("/")}>
                                    Back to Home
                                </Button>
                            </Flex>
                        )}
                    </Card>

                    {step < 3 && (
                        <Flex gap={4} style={{ marginTop: 16 }}>
                            <Text type="secondary">Already have an account?</Text>
                            <Link onClick={() => navigate("/login")}>Sign In</Link>
                        </Flex>
                    )}
                </div>
            </Flex>

            <Footer />
        </>
    );
};

export default DriverRegisterPage;